import BossBottomTab from "../components/boss-bottom-tab";
import { useQuery } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope } from "react-icons/fa";
import HeaderPreorderSales from "../components/header/header-preorder-sales";
import { clsx } from "clsx";

import GridMenu from "./components/grid-menu";
import GuideBottomSheet from "./components/guide-bottom-sheet";
import PreOrderWeeklySchedule from "./components/pre-order-weekly-schedule";
import ReserveCalendar from "./components/reserve-calendar";
import PreOrderList from "./components/pre-order-list";
import QuantityCounter from "./components/quantity-counter";
import { GET_SELLER_STORE } from "../api/seller-store";
import PriceCounter from "./components/price-counter";
import ThreeGridMenu from "./components/three-grid-menu";
import { ReserveExceptionProvider } from "./context/reserve-exception-provider";
import { GET_NOTICE } from "../page";
import { NoticeModal } from "../components/notice-modal";
import StoreTitle from "../components/store-title";
import { GET_PACK_ORDERS_TODAY_TOMORROW } from "../api/pack-order-queries";
import { Order } from "../../../__generated__/graphql";
import { notifyError } from "../../../utils/toast";

const processIsExpanded = () => {
  const isExpanded = localStorage.getItem("isExpanded");
  if (isExpanded === "true") {
    return true;
  }
  return false;
};

const PreOrderHome = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(processIsExpanded);
  const { data: storeData } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });
  const { data: packOrderData } = useQuery(GET_PACK_ORDERS_TODAY_TOMORROW, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      // 팩 주문이 0개면 빈 배열 반환, null이면 오류 발생
      if (data.packOrdersBySellerTodayTomorrow === null) {
        notifyError(
          "새로고침 후 다시 시도해주세요!\n문제가 지속되면 카카오채널로 문의 부탁드려요."
        );
      }
    },
  });
  const { data: noticeData } = useQuery(GET_NOTICE, {
    fetchPolicy: "cache-and-network",
  });

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(false);
  const [isOverlayModeStarted, setIsOverlayModeStarted] = useState(false); // 하위 주문현황 컴포넌트에서 선택하여 취소하기 모드 시작 여부, root 페이지에서 음영 처리를 위해 사용

  const handleOpenBottomSheet = () => {
    setIsBottomSheetOpen(true);
  };

  const toggleDisplay = () => {
    setIsExpanded((prev) => !prev);
    if (isExpanded) {
      localStorage.setItem("isExpanded", "false");
    } else {
      localStorage.setItem("isExpanded", "true");
    }
  };

  // 확정도 취소도 되지 않은 주문
  const pendingCount = packOrderData?.packOrdersBySellerTodayTomorrow?.filter(
    (order: Order) => !order.isPackConfirmed && !order.isCanceled
  ).length;

  // 확정된 주문
  const confirmedCount = packOrderData?.packOrdersBySellerTodayTomorrow?.filter(
    (order: Order) => order.isPackConfirmed
  ).length;

  return (
    <ReserveExceptionProvider>
      <div
        className={clsx(
          "w-full min-h-screen h-full overflow-y-scroll mx-auto bg-gray-100 py-[65px] pb-20",
          isOverlayModeStarted && "pointer-events-none"
        )}
      >
        <HeaderPreorderSales
          toggleDisplay={toggleDisplay}
          isExpanded={isExpanded}
        />
        <StoreTitle title={storeData?.getSellerStore?.title} />

        <div className="flex flex-col justify-center items-center w-full space-y-4  pb-10">
          <h1 className="text-4xl font-bold text-center mt-2 text-gray-800">
            📅 예약 판매
            <p className="text-md text-center text-gray-600">
              상시 오픈, 남는 게 없다면 취소!
            </p>
          </h1>
          {/* 날짜 표시 ex. '6/30 월요일' */}
          <PreOrderWeeklySchedule />

          {noticeData?.latestNotice && (
            <div
              id="공지사항"
              onClick={() => {
                setIsNoticeModalOpen(true);
              }}
              className="px-4 pb-4"
            >
              <div className="w-full h-full bg-white rounded-lg shadow-md px-4 py-2">
                <div className="flex items-center justify-center gap-2">
                  <div className="text-xl font-bold">📢</div>
                  <div className="text-xl font-bold underline underline-offset-4">
                    {noticeData?.latestNotice?.title}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* <BossBannerTop /> */}
          <div className="px-4 flex flex-col justify-center items-center w-full space-y-4  pb-10">
            {isExpanded ? (
              <>
                <PreOrderList
                  setIsOverlayModeStarted={setIsOverlayModeStarted}
                />
                {storeData?.getSellerStore?.pack && (
                  <>
                    <QuantityCounter
                      quantity={storeData?.getSellerStore?.pack?.quantity || 0}
                    />
                    <PriceCounter
                      price={storeData?.getSellerStore?.pack?.price || 0}
                      discountRate={
                        storeData?.getSellerStore?.pack?.discountRate != null
                          ? storeData.getSellerStore.pack.discountRate / 100
                          : 0.6
                      }
                    />
                  </>
                )}
              </>
            ) : (
              <GridMenu
                pendingCount={pendingCount}
                confirmedCount={confirmedCount}
                handleOpenBottomSheet={handleOpenBottomSheet}
              />
            )}
            <ReserveCalendar />
            {isExpanded ? <ThreeGridMenu /> : <></>}
            {/* 의견/건의사항 - 그리드 밑 화면 가득 */}
            <button
              onClick={() => navigate("/boss/feedback")}
              className="w-full bg-white text-[#1562fc] rounded-lg shadow-md hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 py-4 px-4"
            >
              <FaEnvelope className="w-5 h-5 text-blue-400 shrink-0" />
              <span className="text-base font-semibold">의견/건의사항</span>
            </button>
          </div>
        </div>
        <BossBottomTab />
        <GuideBottomSheet
          isOpen={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
        />

        <NoticeModal
          isOpen={isNoticeModalOpen}
          onClose={() => setIsNoticeModalOpen(false)}
          src={noticeData?.latestNotice?.pageUrl}
          title={noticeData?.latestNotice?.title}
        />
      </div>

      {/* 선택하여 취소하기 시작 시 생성되는 음영 레이어 */}
      {isOverlayModeStarted && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-10 pointer-events-none" />
      )}
    </ReserveExceptionProvider>
  );
};

export default PreOrderHome;
